import useDateLocale from '@/common/hooks/useDateLocale'
import { usePathInOrg } from '@/org/hooks/usePathInOrg'
import { Button, Tooltip } from '@chakra-ui/react'
import { textEllipsis } from '@utils/textEllipsis'
import { format } from 'date-fns'
import React from 'react'
import { Link as ReachLink } from 'react-router'

// Where a next step comes from: the tension that produced it, and the meeting
// it was taken in. Rounded clickable buttons, the same kind as the role's chip
// — the model Yohan pointed at is the Tensions page, where the right-hand block
// is a row of chips aligned on a common edge (ENR06-D9, D10).
//
// 🔑 Both render NOTHING when their object is missing, and that absence covers
// two different cases: a step born without a tension (G2-D19), and a tension the
// reader has no right to see (D13). They MUST stay indistinguishable — saying
// "no tension" in the first case would turn a missing chip into the sign that a
// hidden one exists. This is why neither takes a fallback label (ENR06-D11).

interface ThreadChipProps {
  thread?: { id: string; title: string } | null
}

export function NextStepThreadChip({ thread }: ThreadChipProps) {
  const path = usePathInOrg(`threads/${thread?.id}`)
  if (!thread) return null

  return (
    <Tooltip label={thread.title} openDelay={400}>
      <Button
        as={ReachLink}
        to={path}
        size="xs"
        variant="outline"
        borderRadius="full"
        fontWeight="normal"
        flexShrink={0}
      >
        {textEllipsis(thread.title, 20)}
      </Button>
    </Tooltip>
  )
}

interface MeetingChipProps {
  meeting?: { id: string; startDate: string } | null
}

export function NextStepMeetingChip({ meeting }: MeetingChipProps) {
  const path = usePathInOrg(`meetings/${meeting?.id}`)
  const dateLocale = useDateLocale()
  if (!meeting) return null

  return (
    <Button
      as={ReachLink}
      to={path}
      size="xs"
      variant="outline"
      borderRadius="full"
      fontWeight="normal"
      flexShrink={0}
    >
      {/* The DATE, never the meeting's name (ENR06-D10): recurring meetings all
          carry the same one, so two chips reading "Réunion Récurrente" side by
          side would say nothing, where two dates tell them apart. */}
      {format(new Date(meeting.startDate), 'P', { locale: dateLocale })}
    </Button>
  )
}
